import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { api } from './api/client';
import { useMe } from './api/hooks';
import { isLikelyTesla } from './platform';

const ArticleList = lazy(() => import('./routes/ArticleList'));
const ArticleReader = lazy(() => import('./routes/ArticleReader'));
const Login = lazy(() => import('./routes/Login'));
const Callback = lazy(() => import('./routes/Callback'));
const LinkRoute = lazy(() => import('./routes/Link'));
const Approve = lazy(() => import('./routes/Approve'));
const Settings = lazy(() => import('./routes/Settings'));

// Phone sessions have no hard wall-clock cutoff on the backend (see
// requireAuth in jwt.ts) — they're kept alive purely by this periodic
// POST /auth/refresh sliding the cookie's 7-day expiry forward. Once-per-day
// is comfortably often enough relative to that 7-day window while staying
// well clear of spamming the endpoint on every route change; localStorage
// (not module state) so the throttle survives a full page reload too.
const REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000;
const LAST_REFRESH_KEY = 'nr_last_session_refresh';

function Protected({ children }: { children: React.ReactNode }) {
  const me = useMe();

  useEffect(() => {
    if (!me.data) return;
    let last = 0;
    try {
      last = Number(localStorage.getItem(LAST_REFRESH_KEY) ?? 0);
    } catch {
      // Private browsing / storage disabled — just refresh every time.
    }
    if (Date.now() - last < REFRESH_INTERVAL_MS) return;
    void api('/auth/refresh', { method: 'POST' })
      .then(() => {
        try {
          localStorage.setItem(LAST_REFRESH_KEY, String(Date.now()));
        } catch {
          // Best-effort; a missed write just means we retry next mount.
        }
      })
      .catch(() => {
        // Best-effort — a failed refresh here just means the cookie's own
        // expiry (or the hard Tesla cap) is what eventually signs the user
        // out, same as before this existed.
      });
  }, [me.data]);

  if (me.isLoading) return <Loading />;
  if (me.isError || !me.data) {
    return <Navigate to={isLikelyTesla() ? '/link' : '/login'} replace />;
  }
  return <>{children}</>;
}

function Loading() {
  return <div className="loading">…</div>;
}

// Autoplay navigates directly from one /article/:id to the next without
// passing through the list route. React Router reuses the same element
// instance across that param change (same route, same tree position), so
// without a key, Player/ArticleReader state (prompt, countdown, audio
// element) would carry over from the previous article. Keying on :id forces
// a full remount — the same clean-slate behavior as opening a fresh article
// from the list.
function ArticleReaderRoute() {
  const { id } = useParams<{ id: string }>();
  return <ArticleReader key={id} />;
}

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<Callback />} />
        <Route path="/link" element={<LinkRoute />} />
        <Route path="/approve" element={<Approve />} />
        <Route
          path="/"
          element={
            <Protected>
              <ArticleList />
            </Protected>
          }
        />
        <Route
          path="/article/:id"
          element={
            <Protected>
              <ArticleReaderRoute />
            </Protected>
          }
        />
        <Route
          path="/settings"
          element={
            <Protected>
              <Settings />
            </Protected>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
